/**
 * ACTIVITY-CORE (L3, unobtrusive) — "Workspace activity" page: who did what in
 * the workspace (edits to entries, projects, memberships). UNOBTRUSIVE: lives in
 * settings, not in primary navigation. Local-first source: `activity_recent` bucket
 * (~90 days); older history is paginated on demand from the API.
 */
import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { createMemo, createSignal, Show } from "solid-js";
import { History } from "@fleet/icons";
import { type ActivityRow, getActivityFeedQuery, queryKeys } from "@time-tracker/shared";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { chronosRoute } from "../chronos";
import { PageContainer, PageHeader } from "@fleet/ui";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { api } from "@/lib/api";
import { ActivityList } from "@/features/activity/components/activity-list";

const LOCAL_FEED_LIMIT = 200;
const API_PAGE_SIZE = 50;

interface ApiActivityRow {
  id: string;
  scopeId: string;
  listId?: string | null;
  entityType: string;
  entityId: string;
  action: string;
  actorUserId: string;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs: string;
  clientTs?: string | null;
  patch: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}

/** API row (camelCase) → local snake_case feed shape. */
function mapApiActivityRow(item: ApiActivityRow): ActivityRow {
  return {
    id: item.id,
    scope_id: item.scopeId,
    list_id: item.listId ?? null,
    entity_type: item.entityType,
    entity_id: item.entityId,
    action: item.action,
    actor_user_id: item.actorUserId,
    actor_name: item.actorName ?? null,
    actor_device_id: item.actorDeviceId ?? null,
    platform: item.platform ?? null,
    server_ts: item.serverTs,
    client_ts: item.clientTs ?? null,
    patch: typeof item.patch === "string" ? item.patch : JSON.stringify(item.patch ?? {}),
    batch_id: item.batchId ?? null,
    reverts_activity_id: item.revertsActivityId ?? null,
  };
}

function ChronosActivityPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();

  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getActivityFeedQuery(dbInstance, { limit: LOCAL_FEED_LIMIT }) : null;
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...olderRows().filter((r) => !seen.has(r.id))];
  });

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const last = rows()[rows().length - 1];
      const { data, error } = await api.GET("/api/activity", {
        params: { query: { before: last?.id ?? null, limit: API_PAGE_SIZE } },
      });
      if (error || !data) {
        showToast(t(msg`Could not load older activity`), "error");
        return;
      }
      const mapped = data.items.map((item) => mapApiActivityRow(item as ApiActivityRow));
      setOlderRows((prev) => [...prev, ...mapped]);
      setHasMoreOlder(Boolean(data.nextBefore));
    } catch {
      showToast(t(msg`Could not load older activity`), "error");
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <PageContainer maxWidth="3xl">
      <PageHeader
        title={t(msg`Workspace activity`)}
        description={t(msg`Who edited what: time blocks, projects and members.`)}
        backTo="/chronos/settings"
        icon={History}
      />
      <div class="mt-6 space-y-6" data-testid="activity-page">
        <ActivityList
          rows={rows}
          isLoading={() => localQuery.isFetching && (localQuery.data ?? []).length === 0}
          grouped
          currentUserId={userId}
          canRevert
        />

        <Show when={rows().length >= API_PAGE_SIZE && hasMoreOlder()}>
          <div class="flex justify-center pb-6">
            <Button
              variant="outline"
              size="sm"
              disabled={loadingOlder()}
              onClick={() => void loadOlder()}
              data-testid="activity-load-older"
            >
              {loadingOlder() ? t(msg`Loading…`) : t(msg`Load older activity`)}
            </Button>
          </div>
        </Show>
      </div>
    </PageContainer>
  );
}

export const chronosActivityRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/activity",
  component: ChronosActivityPage,
});
